import { useImages } from '@/services/image.service';
import { SampleGraphs, useSampleGraphs } from '@/services/sample.service';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useMemo } from 'react';
import { GhostCurves, PendingNote } from './AnalysisPending';

// The curves shown, mapped from the keys in graphs.json. Each gets its own
// (hidden, auto-scaled) Y axis so they stay individually readable despite very
// different magnitudes; the unit is shown in the tooltip, which lists them in
// this order. graphs.json also
// carries root_area_mm2, left out: over a fixed scan area it is RSD again, in
// absolute terms.
const SERIES = [
    {
        sourceKey: 'rld_cm-1',
        dataKey: 'rld',
        label: 'Root length density',
        unit: 'cm/cm²',
        color: '#2d6a4f',
    },
    {
        sourceKey: 'rsd',
        dataKey: 'rsd',
        label: 'Root surface density',
        unit: 'cm²/cm²',
        color: '#3b82f6',
    },
    // Branch density is hidden for now: the segmentation is not good enough yet
    // for it to be consistent. Uncomment to show it again.
    // {
    //     sourceKey: 'branch_density_cm-1',
    //     dataKey: 'branch',
    //     label: 'Branch density',
    //     unit: 'cm⁻¹',
    //     color: '#d97706',
    // },
] as const;

// Public metadata for the curves, so callers (e.g. the studio-tabs view) can
// build series pickers without duplicating keys/labels/colors.
export const GRAPH_SERIES = SERIES.map((s) => ({
    key: s.dataKey,
    label: s.label,
    color: s.color,
}));

/**
 * Keys (GRAPH_SERIES `key`) of the curves that have at least one point to
 * draw. A curve without any gets no legend entry: there is nothing to name.
 */
export function seriesWithData(
    graphs: SampleGraphs | null | undefined,
): Set<string> {
    return new Set(
        SERIES.filter((s) =>
            (graphs?.series?.[s.sourceKey] ?? []).some(
                (v) => v != null && Number.isFinite(v),
            ),
        ).map((s) => s.dataKey),
    );
}

export default function ImageGraph({
    currentIndex,
    onIndexChange,
}: {
    currentIndex: number;
    onIndexChange?: (index: number) => void;
}) {
    const { t } = useTranslation();
    const { projectId, sampleId } = useParams();
    const { data: images, isLoading: imagesLoading } = useImages(
        projectId,
        sampleId,
    );
    const { data: graphs, isLoading: graphsLoading } = useSampleGraphs(
        projectId,
        sampleId,
    );

    const chartData = useMemo(() => {
        if (!graphs?.date_utc) return [];
        return graphs.date_utc
            .map((d, i) => ({
                date: new Date(d).getTime(),
                ...Object.fromEntries(
                    SERIES.map((s) => [
                        s.dataKey,
                        graphs.series[s.sourceKey]?.[i] ?? null,
                    ]),
                ),
            }))
            .sort((a, b) => a.date - b.date);
    }, [graphs]);

    // Span the axis over EVERY acquisition, not just the computed points. When
    // the latest images have no curve data yet (computations are run manually and
    // often lag behind), the time axis still covers them and the curves simply
    // end where the data stops — no truncated axis, no abrupt rescale.
    const [minTs, maxTs, padding] = useMemo(() => {
        const dates = [
            ...chartData.map((d) => d.date),
            ...(images ?? []).map((i) =>
                new Date(i.acquisitionDate).getTime(),
            ),
        ];
        if (dates.length === 0) return [0, 0, 0];
        const min = Math.min(...dates);
        const max = Math.max(...dates);
        return [min, max, (max - min) * 0.05];
    }, [chartData, images]);

    if (imagesLoading || graphsLoading) {
        return <Skeleton className="w-full h-full" />;
    }

    if (chartData.length === 0) {
        return (
            <GhostCurves>
                <PendingNote>
                    {t('The root analysis will appear here.')}
                </PendingNote>
            </GhostCurves>
        );
    }

    // The reference line marks the acquisition currently shown in the viewer.
    const refX = images?.[currentIndex]
        ? new Date(images[currentIndex].acquisitionDate).getTime()
        : undefined;

    // Clicking a curve point jumps the viewer to the image whose acquisition date
    // is closest to that point (graph dates and image dates are the same set).
    const handleChartClick = (data: { activeLabel?: string | number }) => {
        if (!data?.activeLabel || !onIndexChange || !images) return;
        const clicked = Number(data.activeLabel);
        let best = -1;
        let bestDiff = Infinity;
        images.forEach((img, i) => {
            const diff = Math.abs(
                new Date(img.acquisitionDate).getTime() - clicked,
            );
            if (diff < bestDiff) {
                bestDiff = diff;
                best = i;
            }
        });
        if (best !== -1) onIndexChange(best);
    };

    return (
        <div className="w-full h-full">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={chartData}
                    margin={{ top: 4, right: 16, left: 16, bottom: 0 }}
                    onClick={handleChartClick}
                    // The pointer says the chart can be clicked: nothing else
                    // does. Set here, not on a parent: recharts puts an inline
                    // cursor: default on its own wrapper, which only this
                    // style prop overrides.
                    style={onIndexChange ? { cursor: 'pointer' } : undefined}
                >
                    <defs>
                        {SERIES.map((s) => (
                            <linearGradient
                                key={s.dataKey}
                                id={`fill-${s.dataKey}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor={s.color}
                                    stopOpacity={0.35}
                                />
                                <stop
                                    offset="100%"
                                    stopColor={s.color}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                        ))}
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#ecede8"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="date"
                        type="number"
                        scale="time"
                        domain={[minTs - padding, maxTs + padding]}
                        tickFormatter={(ts: number) =>
                            new Date(ts).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                            })
                        }
                        tick={{ fontSize: 11, fill: '#7a8077' }}
                        stroke="#dadbd5"
                    />
                    {SERIES.map((s) => (
                        <YAxis
                            key={s.dataKey}
                            yAxisId={s.dataKey}
                            domain={['dataMin - 5%', 'dataMax + 5%']}
                            hide
                        />
                    ))}
                    {/* The marker's own axis: hung on a curve's, it vanished
                        whenever that curve had no data to set the scale. No
                        curve sits on this one either, and recharts 3 only takes
                        a dataless axis's domain as given with allowDataOverflow
                        — without it the axis gets no scale and the line is
                        never drawn. */}
                    <YAxis
                        yAxisId="marker"
                        domain={[0, 1]}
                        allowDataOverflow
                        hide
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #dadbd5',
                            borderRadius: '6px',
                            fontSize: '12px',
                        }}
                        labelStyle={{ color: '#1c2620', fontWeight: 600 }}
                        // No slide-in: it would trail behind the hover line,
                        // which moves at once.
                        isAnimationActive={false}
                        // The hover line is the position marker below, fainter:
                        // it snaps to the point a click would jump to, so it
                        // reads as "you would go here" next to "you are here".
                        cursor={{
                            stroke: '#1c2620',
                            strokeOpacity: 0.3,
                            strokeWidth: 1.5,
                            strokeDasharray: '4 4',
                        }}
                        // recharts sorts by name by default, which would not
                        // follow SERIES (and would reshuffle per language).
                        itemSorter={(item) =>
                            SERIES.findIndex((s) => s.dataKey === item.dataKey)
                        }
                        labelFormatter={(ts: number) =>
                            new Date(ts).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                        }
                        formatter={(
                            value: number,
                            name: string,
                            item: { dataKey?: unknown },
                        ) => {
                            if (value == null) return ['—', name];
                            const unit = SERIES.find(
                                (s) => s.dataKey === item.dataKey,
                            )?.unit;
                            return [
                                `${Number(value).toPrecision(3)} ${unit}`,
                                name,
                            ];
                        }}
                    />

                    {SERIES.map((s) => (
                        <Area
                            key={s.dataKey}
                            yAxisId={s.dataKey}
                            type="monotone"
                            dataKey={s.dataKey}
                            stroke={s.color}
                            strokeWidth={2}
                            fill={`url(#fill-${s.dataKey})`}
                            name={t(s.label)}
                            connectNulls={false}
                        />
                    ))}

                    {refX != null && (
                        <ReferenceLine
                            x={refX}
                            yAxisId="marker"
                            stroke="#1c2620"
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}
